import type { Account } from './types';
import { StarIcon } from '../ui/StarIcon';
import { getScoreColors } from '../../lib/scoreColors';
import { propensityFire, propensityFireCount } from '../../lib/propensityFire';

interface AccountListItemProps {
  account: Account;
  isSelected: boolean;
  onSelect: () => void;
  onToggleStar: () => void;
}

/**
 * Fixed row height clamps AccountListItem so it can be virtualized with a
 * uniform estimateSize. Fit + Propensity sit side by side on one line (the 🔥
 * flames are shrunk to text-[8px] so they fit): px-2.5 py-2 padding (16px) +
 * text-xs name line (~16px) + one text-[10px] score line (~14px) + text-[10px]
 * item-count line (~14px) + 1px border ≈ 61px. Using 64px with overflow-hidden
 * keeps rows uniform. Keep in sync with ROW_HEIGHT in AccountSidebar.tsx.
 */
export const ACCOUNT_LIST_ITEM_HEIGHT = 64;

export function AccountListItem({ account, isSelected, onSelect, onToggleStar }: AccountListItemProps) {
  return (
    <button
      onClick={onSelect}
      style={{ height: ACCOUNT_LIST_ITEM_HEIGHT }}
      className={`
        w-full px-2.5 py-2 text-left border-b border-border-tertiary
        overflow-hidden
        transition-colors duration-100
        ${isSelected ? 'bg-bg-green-light' : 'hover:bg-bg-secondary'}
      `}
    >
      <div className="flex items-start gap-2">
        <div
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar();
          }}
          className="flex-none mt-0.5 cursor-pointer"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggleStar();
            }
          }}
        >
          <StarIcon filled={account.isStarred || false} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1.5">
            <span className={`text-xs truncate ${isSelected ? 'font-medium text-content-success' : 'text-content-primary'}`}>
              {account.accountName}
            </span>
            {account.unreadCount > 0 && (
              <span className="flex-none w-4 h-4 rounded-full bg-matcha-700 flex items-center justify-center text-[9px] font-medium text-white">
                {account.unreadCount > 9 ? '9+' : account.unreadCount}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-icon-subtle">
            <span className="whitespace-nowrap">
              Fit Score:{' '}
              {account.fitScore != null ? (
                <span className={`font-semibold ${getScoreColors(account.fitScore).text}`}>{account.fitScore}</span>
              ) : (
                <span>—</span>
              )}
            </span>
            {account.propensityScore != null && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                Propensity Score:
                <span
                  className="text-[8px] leading-none tracking-[-0.08em]"
                  aria-label={`${propensityFireCount(account.propensityScore)} of 5 flames`}
                >
                  {propensityFire(account.propensityScore)}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-icon-subtle">
            {account.hasApprovals && (
              <span className="text-amber-600">Pending</span>
            )}
            <span>{account.unreadCount} {account.unreadCount === 1 ? 'item' : 'items'}</span>
          </div>
        </div>
      </div>
    </button>
  );
}
